import * as React from 'react'
import { FunctionComponent, ReactElement, useMemo, useRef, useState } from 'react'
import HammockDialog, { HammockDialogProps } from './HammockDialog'
import { Formik, FormikHelpers } from 'formik'
import Form from '../../../components/Form/Form'
import { FormikConfig } from 'formik/dist/types'
import { Schema } from 'yup'
import { normalizeAxiosError, NormalizedError } from '../../../reducers/errorReducer'

export type HammockFormDialogProps<E, DialogBodyPropsT = unknown> = Omit<HammockDialogProps<DialogBodyPropsT>, 'dialogBody' | 'dialogBodyProps'> & {
    dialogFormProps?: () => FormikConfig<E>
    dialogFormBody: FunctionComponent<HammockFormDialogProps<E, DialogBodyPropsT>>
    dialogFormBodyProps?: () => DialogBodyPropsT
    setOnSubmit?: (onSubmit: FormikConfig<E>['onSubmit']) => void
    setValidationSchema?: (schema: FormikConfig<E>['validationSchema']) => void
}

/**
 * A little more complex Dialog to support a Form. The DialogSurface will contain a Formik form and provides for an onSubmit + validation.
 *
 * @param props all optional props, except dialogBody and dialogFormBody.
 */
function HammockFormDialogBody<E, DialogBodyPropsT = unknown>(props: HammockFormDialogProps<E, DialogBodyPropsT>): ReactElement {
    const dialogFormProps = useMemo(() => props.dialogFormProps?.() ?? ({} as FormikConfig<E>), [props])

    const localOnSubmitRef = useRef<FormikConfig<E>['onSubmit']>(dialogFormProps.onSubmit)

    const [globalErrors, setGlobalErrors] = useState<NormalizedError[]>()
    const [validationSchema, setValidationSchema] = useState<Schema>(dialogFormProps.validationSchema)

    return (
        <Formik<E>
            validateOnChange={false}
            validateOnBlur={false}
            {...dialogFormProps}
            onSubmit={(values: E, formikHelpers: FormikHelpers<E>) => {
                let localOnSubmit = dialogFormProps.onSubmit
                if (localOnSubmitRef.current) {
                    localOnSubmit = localOnSubmitRef.current
                }

                return Promise.resolve()
                    .then(() => localOnSubmit?.(values, formikHelpers))
                    .then(
                        (keepOpen: boolean) => keepOpen !== true && props.onDismiss?.(),
                        e => setGlobalErrors([normalizeAxiosError(e)]),
                    )
            }}
            validationSchema={validationSchema}
        >
            <Form globalErrors={globalErrors}>
                <props.dialogFormBody
                    {...props}
                    setOnSubmit={(fn: FormikConfig<E>['onSubmit']) => (localOnSubmitRef.current = fn)}
                    setValidationSchema={(schema: FormikConfig<E>['validationSchema']) => setValidationSchema(schema)}
                />
            </Form>
        </Formik>
    )
}

export default function HammockFormDialog<E, DialogBodyPropsT = unknown>(props: HammockFormDialogProps<E, DialogBodyPropsT>): ReactElement {
    return <HammockDialog<DialogBodyPropsT> {...props} dialogBody={HammockFormDialogBody as any} />
}
